/**
 * Sidebar variant rendered on /settings/* routes. Visually identical to
 * the main app Sidebar (same navy + gold tokens, same brand block, same
 * mobile drawer) — only the nav items differ: the settings sections
 * replace the workspace modules.
 *
 * The main AppLayout picks between Sidebar and SettingsSidebar by route
 * prefix so users on settings see ONLY this nav (no double sidebar).
 */

import { hasPermission } from '@/hooks/usePermission'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/stores/auth.store'
import { useUIStore } from '@/stores/ui.store'
import { Link, useLocation } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowLeft,
  Banknote,
  BookOpen,
  Building2,
  Calculator,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  GraduationCap,
  Layers,
  Lock,
  type LucideIcon,
  Mail,
  Palette,
  Settings2,
  Sliders,
  UserCircle,
  UserCog,
  Users,
  X,
} from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface SettingsNavItem {
  to: string
  labelKey: string
  group: 'account' | 'organization'
  icon: LucideIcon
  permission?: string
}

const ITEMS: SettingsNavItem[] = [
  { to: '/settings/profile', labelKey: 'nav.profile', group: 'account', icon: UserCircle },
  { to: '/settings/security', labelKey: 'nav.security', group: 'account', icon: Lock },
  { to: '/settings/preferences', labelKey: 'nav.preferences', group: 'account', icon: Settings2 },
  {
    to: '/settings/organization',
    labelKey: 'nav.organization',
    group: 'organization',
    icon: Building2,
    permission: 'org:read',
  },
  {
    // L6 — mon abonnement (facturation SaaS, packs, consommation).
    to: '/settings/subscription',
    labelKey: 'nav.subscription',
    group: 'organization',
    icon: CreditCard,
  },
  {
    to: '/settings/users',
    labelKey: 'nav.users',
    group: 'organization',
    icon: Users,
    permission: 'users:read',
  },
  {
    to: '/settings/tariffs',
    labelKey: 'nav.tariffs',
    group: 'organization',
    icon: Layers,
    permission: 'tariffs:manage',
  },
  {
    to: '/settings/documents',
    labelKey: 'nav.documents',
    group: 'organization',
    icon: FileText,
    permission: 'org:update',
  },
  {
    to: '/settings/email',
    labelKey: 'nav.email',
    group: 'organization',
    icon: Mail,
    permission: 'org:update',
  },
  {
    to: '/settings/chart-of-accounts',
    labelKey: 'nav.chart_of_accounts',
    group: 'organization',
    icon: BookOpen,
    permission: 'chart_of_accounts:manage',
  },
  {
    to: '/settings/accounting',
    labelKey: 'nav.accounting',
    group: 'organization',
    icon: Calculator,
    permission: 'chart_of_accounts:manage',
  },
  {
    to: '/settings/payroll',
    labelKey: 'nav.payroll_parameters',
    group: 'organization',
    icon: Banknote,
    permission: 'payroll:parameters',
  },
  {
    to: '/settings/hr',
    labelKey: 'nav.hr_catalog',
    group: 'organization',
    icon: UserCog,
    permission: 'hr_catalog:manage',
  },
  {
    to: '/settings/branding',
    labelKey: 'nav.branding',
    group: 'organization',
    icon: Palette,
    permission: 'org:update',
  },
  {
    to: '/settings/learn',
    labelKey: 'nav.learn',
    group: 'organization',
    icon: GraduationCap,
    permission: 'learn:write',
  },
]

function useVisibleItems(): SettingsNavItem[] {
  const perms = useAuthStore((s) => s.permissions)
  return useMemo(
    () => ITEMS.filter((item) => !item.permission || hasPermission(perms, item.permission)),
    [perms],
  )
}

function NavItemLink({
  item,
  active,
  collapsed,
  onNavigate,
  label,
}: {
  item: SettingsNavItem
  active: boolean
  collapsed: boolean
  onNavigate?: () => void
  label: string
}) {
  const Icon = item.icon
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={cn(
        'group relative flex h-10 items-center gap-3 rounded-lg px-3 text-sm transition-colors',
        active
          ? 'bg-sidebar-active font-medium text-sidebar-active-foreground shadow-sm'
          : 'text-sidebar-foreground/75 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
        collapsed && 'h-10 w-10 justify-center p-0',
      )}
      title={collapsed ? label : undefined}
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      {!collapsed ? <span className="truncate">{label}</span> : null}
    </Link>
  )
}

function SettingsBrand({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation('settings')
  return (
    <Link to="/dashboard" className="flex items-center gap-2.5" title={t('title')}>
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sidebar-accent text-sidebar-accent-foreground shadow-sm">
        <Sliders className="h-[18px] w-[18px]" />
      </div>
      {!collapsed ? (
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold tracking-tight text-sidebar-foreground">
            {t('title')}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-sidebar-foreground/60">
            AMANET Core
          </span>
        </div>
      ) : null}
    </Link>
  )
}

function GroupLabel({ children, collapsed }: { children: string; collapsed: boolean }) {
  if (collapsed) {
    return <div className="mx-3 my-2 h-px bg-sidebar-foreground/10" />
  }
  return (
    <div className="mt-4 px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/55 first:mt-0">
      {children}
    </div>
  )
}

function BackToApp({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation('common')
  return (
    <Link
      to="/dashboard"
      onClick={onNavigate}
      className={cn(
        'group flex h-10 items-center gap-3 rounded-lg px-3 text-sm text-sidebar-foreground/65 transition-colors hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
        collapsed && 'h-10 w-10 justify-center p-0',
      )}
      title={collapsed ? t('back') : undefined}
    >
      <ArrowLeft className="h-[18px] w-[18px] shrink-0" />
      {!collapsed ? <span className="truncate">{t('back')}</span> : null}
    </Link>
  )
}

function NavList({
  items,
  collapsed,
  onNavigate,
}: {
  items: SettingsNavItem[]
  collapsed: boolean
  onNavigate?: () => void
}) {
  const { t } = useTranslation('settings')
  const location = useLocation()

  const account = items.filter((i) => i.group === 'account')
  const org = items.filter((i) => i.group === 'organization')

  return (
    <nav className="flex-1 overflow-y-auto px-3 py-3">
      <div className="flex flex-col gap-0.5">
        <BackToApp collapsed={collapsed} onNavigate={onNavigate} />
      </div>

      <GroupLabel collapsed={collapsed}>{t('group.account')}</GroupLabel>
      <div className="flex flex-col gap-0.5">
        {account.map((item) => (
          <NavItemLink
            key={item.to}
            item={item}
            label={t(item.labelKey)}
            active={location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
      </div>

      {org.length > 0 ? (
        <>
          <GroupLabel collapsed={collapsed}>{t('group.organization')}</GroupLabel>
          <div className="flex flex-col gap-0.5">
            {org.map((item) => (
              <NavItemLink
                key={item.to}
                item={item}
                label={t(item.labelKey)}
                active={
                  location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
                }
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </>
      ) : null}
    </nav>
  )
}

export function SettingsSidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggle = useUIStore((s) => s.toggleSidebar)
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen)
  const closeMobile = useUIStore((s) => s.closeMobileSidebar)
  const items = useVisibleItems()

  useEffect(() => {
    if (!mobileOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [mobileOpen])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobile()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileOpen, closeMobile])

  return (
    <>
      {/* Desktop */}
      <aside
        className={cn(
          'sticky top-0 z-30 hidden h-dvh shrink-0 flex-col border-r border-sidebar-border bg-sidebar-background/95 backdrop-blur-sm transition-[width] duration-200 md:flex',
          collapsed ? 'w-[68px]' : 'w-64',
        )}
      >
        <div
          className={cn(
            'flex h-16 items-center border-b border-sidebar-border',
            collapsed ? 'justify-center px-2' : 'px-4',
          )}
        >
          <SettingsBrand collapsed={collapsed} />
        </div>
        <NavList items={items} collapsed={collapsed} />
        <button
          type="button"
          onClick={toggle}
          className="flex h-11 w-full items-center justify-center gap-2 border-t border-sidebar-border text-xs text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/40 hover:text-sidebar-foreground"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      {/* Mobile drawer — same animation + overlay as the workspace Sidebar so
          users see no behavioural difference when the burger is tapped. */}
      <AnimatePresence>
        {mobileOpen ? (
          // biome-ignore lint/a11y/useSemanticElements: mobile drawer — using <dialog> would override the framer-motion overlay + aside siblings
          <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0 bg-background/50 backdrop-blur-sm"
              onClick={closeMobile}
              aria-hidden="true"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'tween', duration: 0.2 }}
              className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r border-sidebar-border bg-sidebar-background shadow-xl"
            >
              <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
                <SettingsBrand collapsed={false} />
                <button
                  type="button"
                  onClick={closeMobile}
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-sidebar-foreground/70 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <NavList items={items} collapsed={false} onNavigate={closeMobile} />
            </motion.aside>
          </div>
        ) : null}
      </AnimatePresence>
    </>
  )
}
